/**
 * Protected route component to guard authenticated routes
 * 
 * @author Claude
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route wrapper that checks authentication
 * @param {ProtectedRouteProps} props - Component props
 * @returns {JSX.Element} Protected route or redirect to login
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Show loading spinner while checking auth
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100vh',
        }}
        aria-live="polite"
        aria-label="Loading authentication"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render the children when authenticated
  return <>{children}</>;
};

export default ProtectedRoute;